/**
 * Migration warnings surfaced in the editor once, right after an HTML import,
 * so re-host failures ("3 images stayed external because they were blocked")
 * and formatting notes are never silent.
 *
 * Two layers: a per-code SUMMARY (image-rehost codes get specific i18n lines,
 * everything else collapses into one "formatting notes" count) and, behind a
 * "Show details" toggle, the full per-warning message list — so the user can
 * actually read WHAT each note was, not just how many. Dismissible.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { MigrationWarning } from "../lib/migrations";

const IMAGE_CODE_KEYS: Record<string, string> = {
  IMAGE_REHOST_BLOCKED: "image_rehost_blocked",
  IMAGE_REHOST_FAILED: "image_rehost_failed",
  IMAGE_REHOST_UNSUPPORTED_TYPE: "image_rehost_unsupported_type",
  IMAGE_REHOST_TOO_LARGE: "image_rehost_too_large",
  IMAGE_REHOST_LIMIT: "image_rehost_limit",
  IMAGE_REHOST_BUDGET: "image_rehost_budget",
};

// Severity → dot colour. The banner itself is amber; the dot just lets a real
// error stand out from an informational note in the expanded detail list.
const SEVERITY_DOT: Record<MigrationWarning["severity"], string> = {
  error: "bg-danger-500",
  warning: "bg-warning-500",
  info: "bg-gray-400",
};

export function ImportWarningsBanner({
  warnings,
  onDismiss,
}: {
  warnings: MigrationWarning[];
  /**
   * Called when the user dismisses the banner. The host clears the one-time
   * warnings from history state so the banner stays gone after a reload —
   * `history.state` (where these warnings live) survives reloads, so a
   * local-only dismiss reappears on the next load.
   */
  onDismiss?: () => void;
}): JSX.Element | null {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(false);

  if (dismissed || warnings.length === 0) return null;

  const counts = new Map<string, number>();
  for (const w of warnings) {
    counts.set(w.code, (counts.get(w.code) ?? 0) + 1);
  }

  const lines: string[] = [];
  let otherCount = 0;
  for (const [code, count] of counts) {
    const key = IMAGE_CODE_KEYS[code];
    if (key !== undefined) {
      lines.push(t(`import_html.warnings.${key}`, { count }));
    } else {
      otherCount += count;
    }
  }
  if (otherCount > 0) {
    lines.push(t("import_html.warnings.other", { count: otherCount }));
  }

  return (
    <div
      role="status"
      className="flex items-start justify-between gap-3 border-b border-warning-200 bg-warning-50 px-4 py-2 text-sm text-warning-800"
    >
      <div className="min-w-0">
        <p className="font-medium">{t("import_html.warnings_heading")}</p>
        <ul className="mt-1 list-disc pl-5 text-xs">
          {lines.map((line, i) => (
            <li key={i}>{line}</li>
          ))}
        </ul>

        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          data-testid="import-warnings-toggle"
          className="mt-1 text-xs font-medium underline underline-offset-2 hover:text-warning-900"
        >
          {expanded
            ? t("import_html.warnings.hide_details")
            : t("import_html.warnings.show_details", { count: warnings.length })}
        </button>

        {expanded && (
          <ul
            data-testid="import-warnings-details"
            className="mt-2 space-y-1 border-t border-warning-200/60 pt-2 text-xs"
          >
            {warnings.map((w, i) => (
              <li key={i} className="flex items-start gap-2">
                <span
                  className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${SEVERITY_DOT[w.severity]}`}
                  aria-hidden="true"
                />
                <span className="min-w-0 break-words">
                  {w.message === "" ? w.code : w.message}
                  {w.block_id !== undefined && w.block_id !== "" ? (
                    <span className="ml-1 text-warning-600/70">· {w.block_id}</span>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <button
        type="button"
        onClick={() => {
          setDismissed(true);
          onDismiss?.();
        }}
        data-testid="import-warnings-dismiss"
        className="shrink-0 rounded border border-warning-300 px-2 py-0.5 text-xs hover:bg-warning-100"
      >
        {t("import_html.dismiss")}
      </button>
    </div>
  );
}
